/**
 * Probability distributions for the statistics tools.
 *
 * Every function is pure and works in plain numbers. CDFs are lower-tail P(X ≤ x);
 * `*Upper` functions give the upper tail computed directly rather than as 1 − CDF, so a
 * p-value of 3 × 10⁻¹² is reported as that rather than as zero. Quantile functions take
 * a lower-tail probability.
 */

import {
  erfc,
  invertMonotone,
  logChoose,
  logFactorial,
  regularizedBeta,
  regularizedGammaP,
  regularizedGammaQ,
} from "@/lib/stats/special";

/* -------------------------------------------------------------------- normal */

export function normalPdf(x: number, mean = 0, sd = 1): number {
  const z = (x - mean) / sd;
  return Math.exp(-0.5 * z * z) / (sd * Math.sqrt(2 * Math.PI));
}

/** Φ(z) for the standard normal, or P(X ≤ x) given a mean and SD. */
export function normalCdf(x: number, mean = 0, sd = 1): number {
  const z = (x - mean) / sd;
  return 0.5 * erfc(-z / Math.SQRT2);
}

export function normalUpper(x: number, mean = 0, sd = 1): number {
  const z = (x - mean) / sd;
  return 0.5 * erfc(z / Math.SQRT2);
}

/*
 * Acklam's rational approximation (relative error 1.15e-9), polished with one Halley
 * step against the exact CDF, which brings it to full double precision.
 */
const ACKLAM_A = [
  -3.969_683_028_665_376e1, 2.209_460_984_245_205e2, -2.759_285_104_469_687e2,
  1.383_577_518_672_69e2, -3.066_479_806_614_716e1, 2.506_628_277_459_239,
];
const ACKLAM_B = [
  -5.447_609_879_822_406e1, 1.615_858_368_580_409e2, -1.556_989_798_598_866e2,
  6.680_131_188_771_972e1, -1.328_068_155_288_572e1,
];
const ACKLAM_C = [
  -7.784_894_002_430_293e-3, -3.223_964_580_411_365e-1, -2.400_758_277_161_838,
  -2.549_732_539_343_734, 4.374_664_141_464_968, 2.938_163_982_698_783,
];
const ACKLAM_D = [
  7.784_695_709_041_462e-3, 3.224_671_290_700_398e-1, 2.445_134_137_142_996,
  3.754_408_661_907_416,
];
const P_LOW = 0.024_25;

function acklam(p: number): number {
  if (p < P_LOW) {
    const q = Math.sqrt(-2 * Math.log(p));
    return (
      (((((ACKLAM_C[0] * q + ACKLAM_C[1]) * q + ACKLAM_C[2]) * q +
        ACKLAM_C[3]) *
        q +
        ACKLAM_C[4]) *
        q +
        ACKLAM_C[5]) /
      ((((ACKLAM_D[0] * q + ACKLAM_D[1]) * q + ACKLAM_D[2]) * q + ACKLAM_D[3]) *
        q +
        1)
    );
  }
  if (p > 1 - P_LOW) {
    return -acklam(1 - p);
  }
  const q = p - 0.5;
  const r = q * q;
  return (
    ((((((ACKLAM_A[0] * r + ACKLAM_A[1]) * r + ACKLAM_A[2]) * r + ACKLAM_A[3]) *
      r +
      ACKLAM_A[4]) *
      r +
      ACKLAM_A[5]) *
      q) /
    (((((ACKLAM_B[0] * r + ACKLAM_B[1]) * r + ACKLAM_B[2]) * r + ACKLAM_B[3]) *
      r +
      ACKLAM_B[4]) *
      r +
      1)
  );
}

/** z such that Φ(z) = p. */
export function normalQuantile(p: number, mean = 0, sd = 1): number {
  if (p <= 0) {
    return Number.NEGATIVE_INFINITY;
  }
  if (p >= 1) {
    return Number.POSITIVE_INFINITY;
  }
  let x = acklam(p);
  const e = normalCdf(x) - p;
  const u = e * Math.sqrt(2 * Math.PI) * Math.exp((x * x) / 2);
  x -= u / (1 + (x * u) / 2);
  return mean + sd * x;
}

/* ----------------------------------------------------------------- Student t */

/** P(T ≤ t) with `df` degrees of freedom (df may be fractional, as in Welch's test). */
export function studentTCdf(t: number, df: number): number {
  const x = df / (df + t * t);
  const tail = 0.5 * regularizedBeta(x, df / 2, 0.5);
  return t > 0 ? 1 - tail : tail;
}

/** P(T ≥ t). */
export function studentTUpper(t: number, df: number): number {
  const x = df / (df + t * t);
  const tail = 0.5 * regularizedBeta(x, df / 2, 0.5);
  return t > 0 ? tail : 1 - tail;
}

/** Two-sided p-value for an observed t. */
export function studentTTwoTailed(t: number, df: number): number {
  return Math.min(1, 2 * studentTUpper(Math.abs(t), df));
}

export function studentTQuantile(p: number, df: number): number {
  if (p <= 0) {
    return Number.NEGATIVE_INFINITY;
  }
  if (p >= 1) {
    return Number.POSITIVE_INFINITY;
  }
  if (p === 0.5) {
    return 0;
  }
  if (p < 0.5) {
    return -studentTQuantile(1 - p, df);
  }
  // Upper half: find t ≥ 0 with P(T ≥ t) = 1 − p, using the decreasing upper tail.
  return invertMonotone((t) => -studentTUpper(t, df), -(1 - p), 0, 4);
}

/* --------------------------------------------------------------- chi-square */

export function chiSquareCdf(x: number, df: number): number {
  return regularizedGammaP(df / 2, x / 2);
}

export function chiSquareUpper(x: number, df: number): number {
  return regularizedGammaQ(df / 2, x / 2);
}

export function chiSquareQuantile(p: number, df: number): number {
  if (p <= 0) {
    return 0;
  }
  if (p >= 1) {
    return Number.POSITIVE_INFINITY;
  }
  return invertMonotone((x) => chiSquareCdf(x, df), p, 0, Math.max(1, df));
}

/* ------------------------------------------------------------------------ F */

export function fCdf(x: number, d1: number, d2: number): number {
  if (x <= 0) {
    return 0;
  }
  return regularizedBeta((d1 * x) / (d1 * x + d2), d1 / 2, d2 / 2);
}

export function fUpper(x: number, d1: number, d2: number): number {
  if (x <= 0) {
    return 1;
  }
  return regularizedBeta(d2 / (d2 + d1 * x), d2 / 2, d1 / 2);
}

export function fQuantile(p: number, d1: number, d2: number): number {
  if (p <= 0) {
    return 0;
  }
  if (p >= 1) {
    return Number.POSITIVE_INFINITY;
  }
  return invertMonotone((x) => fCdf(x, d1, d2), p, 0, 2);
}

/* ------------------------------------------------------------------ discrete */

/** P(X = k) for X ~ Binomial(n, p). */
export function binomialPmf(k: number, n: number, p: number): number {
  if (k < 0 || k > n || !Number.isInteger(k)) {
    return 0;
  }
  if (p === 0) {
    return k === 0 ? 1 : 0;
  }
  if (p === 1) {
    return k === n ? 1 : 0;
  }
  return Math.exp(
    logChoose(n, k) + k * Math.log(p) + (n - k) * Math.log(1 - p)
  );
}

/** P(X ≤ k) for X ~ Binomial(n, p), through the incomplete beta function. */
export function binomialCdf(k: number, n: number, p: number): number {
  if (k < 0) {
    return 0;
  }
  if (k >= n) {
    return 1;
  }
  const floor = Math.floor(k);
  return regularizedBeta(1 - p, n - floor, floor + 1);
}

/** P(X = k) for X ~ Poisson(λ). */
export function poissonPmf(k: number, lambda: number): number {
  if (k < 0 || !Number.isInteger(k)) {
    return 0;
  }
  if (lambda === 0) {
    return k === 0 ? 1 : 0;
  }
  return Math.exp(k * Math.log(lambda) - lambda - logFactorial(k));
}

/** P(X ≤ k) for X ~ Poisson(λ) = Q(k + 1, λ). */
export function poissonCdf(k: number, lambda: number): number {
  if (k < 0) {
    return 0;
  }
  return regularizedGammaQ(Math.floor(k) + 1, lambda);
}

/**
 * P(X = k) when drawing `draws` items without replacement from a population of
 * `population` that contains `successes` successes.
 */
export function hypergeometricPmf(
  k: number,
  population: number,
  successes: number,
  draws: number
): number {
  const minK = Math.max(0, draws - (population - successes));
  const maxK = Math.min(successes, draws);
  if (k < minK || k > maxK || !Number.isInteger(k)) {
    return 0;
  }
  return Math.exp(
    logChoose(successes, k) +
      logChoose(population - successes, draws - k) -
      logChoose(population, draws)
  );
}

export function hypergeometricCdf(
  k: number,
  population: number,
  successes: number,
  draws: number
): number {
  let total = 0;
  const minK = Math.max(0, draws - (population - successes));
  for (let i = minK; i <= Math.floor(k); i++) {
    total += hypergeometricPmf(i, population, successes, draws);
  }
  return Math.min(1, total);
}
